/**
 * SupplierCrewDashboard ✦ + ApplicantsPage ✦
 *
 * Two routes in one module — shared shell + Crew palette.
 *   /dashboard/supplier/crew                  → list my opportunities
 *   /dashboard/supplier/crew/:id/applicants   → applicants of one
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Plus, ArrowLeft, CheckCircle, X as XIcon, UsersThree, Sparkle, Star, ChatCircleDots, Heart, ArrowClockwise, ShieldCheck, Crown, SealCheck } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const Eyebrow = ({ children }) => (
  <p style={{
    margin: 0, fontFamily: FONT_STACK, fontSize: 11, fontWeight: 700,
    letterSpacing: '0.32em', textTransform: 'uppercase',
    background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  }}>{children}</p>
);

const Page = ({ children, testId }) => (
  <div data-testid={testId} style={{
    minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
    padding: 'clamp(20px, 4vw, 48px) clamp(16px, 5vw, 32px) 80px',
  }}>
    <div style={{
      position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
      background: `radial-gradient(circle at 12% 12%, ${PURPLE}1F 0%, transparent 38%), radial-gradient(circle at 90% 90%, ${PINK}14 0%, transparent 38%)`,
    }} />
    <div style={{ maxWidth: 920, margin: '0 auto', position: 'relative', zIndex: 1 }}>
      {children}
    </div>
  </div>
);

// One-tap reference chips. Six tags, ultra lightweight per spec —
// no paragraphs, no star ratings, no friction. Toggling a chip
// stages it locally; "Send" persists the entire set in a single
// idempotent POST. Re-opens with the already-chosen tags pre-lit
// so a second tap shows "You vouched" not a blank slate.
const REFERENCE_TAG_LABELS = [
  { id: 'reliable',          label: 'Reliable' },
  { id: 'friendly',          label: 'Friendly' },
  { id: 'great_attitude',    label: 'Great Attitude' },
  { id: 'team_player',       label: 'Team Player' },
  { id: 'fast_learner',      label: 'Fast Learner' },
  { id: 'great_with_guests', label: 'Great With Guests' },
];

const ReferenceChips = ({ applicationId, applicantName, onSent }) => {
  const [selected, setSelected] = useState(new Set());
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const loadExisting = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crew/applications/${applicationId}/reference`);
      if (r.data?.reference) {
        setExisting(r.data.reference);
        setSelected(new Set(r.data.reference.tags || []));
      }
    } catch { /* silent — empty state is fine */ }
  }, [applicationId]);

  useEffect(() => {
    if (open && !existing) loadExisting();
  }, [open, existing, loadExisting]);

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const send = async () => {
    if (selected.size === 0) {
      setError('Tap at least one chip');
      return;
    }
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/crew/applications/${applicationId}/reference`, {
        tags: Array.from(selected),
      });
      setExisting({ tags: Array.from(selected) });
      setOpen(false);
      onSent?.(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send reference');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`ref-open-${applicationId}`}
        style={{
          marginTop: 10, padding: '8px 14px', borderRadius: 999,
          border: `1px solid ${existing ? BLUE + '66' : PANEL_BORDER}`,
          background: existing ? `${BLUE}1F` : 'transparent',
          color: existing ? BLUE : TEXT_MUTED, cursor: 'pointer',
          fontFamily: FONT_STACK, fontSize: 12, fontWeight: 800,
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}
      >
        <Star size={12} weight={existing ? 'fill' : 'bold'} />
        {existing
          ? `You vouched · ${(existing.tags || []).length} ${(existing.tags || []).length === 1 ? 'tag' : 'tags'}`
          : 'Leave a reference'}
      </button>
    );
  }

  return (
    <div
      data-testid={`ref-panel-${applicationId}`}
      style={{
        marginTop: 12, padding: 14, borderRadius: 14,
        background: 'rgba(108,59,255,0.08)', border: `1px solid ${PURPLE}33`,
      }}
    >
      <p style={{
        margin: '0 0 10px', fontFamily: FONT_STACK, fontSize: 13,
        fontWeight: 800, color: TEXT,
      }}>
        How was {applicantName?.split(' ')[0] || 'they'}?
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {REFERENCE_TAG_LABELS.map((t) => {
          const isOn = selected.has(t.id);
          return (
            <button
              key={t.id}
              onClick={() => toggle(t.id)}
              data-testid={`ref-chip-${t.id}`}
              data-selected={isOn ? '1' : '0'}
              style={{
                padding: '7px 12px', borderRadius: 999,
                border: `1px solid ${isOn ? PINK : PANEL_BORDER}`,
                background: isOn
                  ? `linear-gradient(135deg, ${PURPLE}55, ${PINK}55)`
                  : 'rgba(255,255,255,0.04)',
                color: isOn ? TEXT : TEXT_MUTED, cursor: 'pointer',
                fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
                boxShadow: isOn ? `0 6px 16px ${PINK}33` : 'none',
                transition: 'all .15s ease',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {error && (
        <p data-testid={`ref-error-${applicationId}`}
          style={{ color: PINK, fontSize: 12, margin: '0 0 8px' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={send}
          disabled={busy}
          data-testid={`ref-send-${applicationId}`}
          style={{
            padding: '9px 16px', borderRadius: 999, border: 'none',
            cursor: busy ? 'wait' : 'pointer', color: TEXT,
            fontFamily: FONT_STACK, fontSize: 12.5, fontWeight: 800,
            background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
            boxShadow: `0 8px 20px ${PURPLE}55`,
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'Sending…' : (existing ? 'Update' : 'Send')}
        </button>
        <button
          onClick={() => setOpen(false)}
          data-testid={`ref-cancel-${applicationId}`}
          style={{
            padding: '9px 14px', borderRadius: 999,
            background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
            color: TEXT_MUTED, cursor: 'pointer',
            fontFamily: FONT_STACK, fontSize: 12, fontWeight: 700,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ── ApplicantInsightsStrip ✦ — supplier-only trust visibility ──
// Subtle glassmorphism row of earned context chips. NOT a leaderboard,
// NOT a percentage, NOT a ranking. Just factual signals: tier label,
// repeat-requested count (only if > 0), favourite marks (only if > 0),
// events completed, and up to two inline badges. Designed to help a
// supplier decide fast without surfacing public hierarchy.
const TIER_COLOUR_LOOKUP = {
  rising:    BLUE,
  trusted:   PURPLE,
  favourite: PINK,
  leader:    '#FFD166',
};
const TIER_ICON_LOOKUP = {
  rising:    Sparkle,
  trusted:   ShieldCheck,
  favourite: Heart,
  leader:    Crown,
};

const ApplicantInsightsStrip = ({ insights, applicationId }) => {
  if (!insights) return null;
  const tierId = insights.tier_id || 'rising';
  const accent = TIER_COLOUR_LOOKUP[tierId] || BLUE;
  const TierIcon = TIER_ICON_LOOKUP[tierId] || Sparkle;
  const repeats = insights.repeat_requests || 0;
  const favs = insights.favourite_marks || 0;
  const events = insights.events_completed || 0;
  const inline = insights.inline_badges || [];

  // Don't render an empty strip — if a brand-new crew member has
  // literally nothing to show, the absence itself is information.
  const hasContent = events > 0 || repeats > 0 || favs > 0 || inline.length > 0 || tierId !== 'rising';
  if (!hasContent) {
    return (
      <div
        data-testid={`applicant-insights-${applicationId}`}
        data-tier={tierId}
        data-empty="1"
        style={{
          marginTop: 8, fontSize: 11.5, color: TEXT_MUTED, fontStyle: 'italic',
        }}
      >
        ✨ New to EventDrop · welcome them
      </div>
    );
  }

  const Chip = ({ children, icon: Icon, accent: chipAccent = TEXT_MUTED, testid }) => (
    <span
      data-testid={testid}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 999,
        background: 'rgba(255,255,255,0.04)',
        border: `1px solid ${chipAccent}33`,
        color: TEXT, fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 700,
      }}
    >
      {Icon && <Icon size={11} weight="fill" color={chipAccent} />}
      {children}
    </span>
  );

  return (
    <div
      data-testid={`applicant-insights-${applicationId}`}
      data-tier={tierId}
      style={{
        marginTop: 8, padding: '8px 10px', borderRadius: 14,
        background: `linear-gradient(135deg, ${accent}0F, rgba(255,255,255,0.02))`,
        border: `1px solid ${accent}33`,
        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
      }}
    >
      <span
        data-testid={`insights-tier-${applicationId}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 10px', borderRadius: 999,
          background: `linear-gradient(135deg, ${accent}22, ${PURPLE}14)`,
          border: `1px solid ${accent}66`,
          color: TEXT, fontFamily: FONT_STACK, fontSize: 11.5, fontWeight: 800,
          boxShadow: `0 0 12px ${accent}33`,
        }}
      >
        <TierIcon size={11} weight="fill" color={accent} />
        {insights.tier_label}
      </span>
      {repeats > 0 && (
        <Chip
          testid={`insights-repeat-${applicationId}`}
          icon={ArrowClockwise}
          accent={BLUE}
        >
          Requested again ×{repeats}
        </Chip>
      )}
      {favs > 0 && (
        <Chip
          testid={`insights-favourite-${applicationId}`}
          icon={Heart}
          accent={PINK}
        >
          {favs === 1 ? 'Favourite ✦' : `Favourite ×${favs}`}
        </Chip>
      )}
      {events > 0 && (
        <Chip
          testid={`insights-events-${applicationId}`}
          icon={Sparkle}
          accent={'#FFD166'}
        >
          {events} {events === 1 ? 'event' : 'events'} completed
        </Chip>
      )}
      {inline.map((b) => (
        <Chip
          key={b.id}
          testid={`insights-badge-${b.id}-${applicationId}`}
          icon={SealCheck}
          accent={accent}
        >
          {b.label}
        </Chip>
      ))}
    </div>
  );
};

// ── InvitePreviousCrewModal ✦ — supplier-side directed invites ──
// Opens a soft-glow panel of suggested crew members (suppliers'
// previous request-again / favourite marks). Multi-select + send.
// Idempotent on the backend; this UI doesn't track "already sent"
// state beyond the suggest endpoint's filter.
const InvitePreviousCrewModal = ({ opportunityId, opportunityTitle, onSent }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentToast, setSentToast] = useState('');

  const [loaded, setLoaded] = useState(false);

  const loadSuggestions = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(
        `${API}/crew/opportunities/${opportunityId}/directed-invites/suggest`
      );
      setSuggestions(r.data?.suggestions || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load suggestions');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [opportunityId]);

  useEffect(() => {
    if (open && !loaded && !loading) loadSuggestions();
  }, [open, loaded, loading, loadSuggestions]);

  const toggle = (id) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const send = async () => {
    if (selected.size === 0) {
      setError('Pick at least one crew member');
      return;
    }
    setBusy(true); setError('');
    try {
      const r = await axios.post(
        `${API}/crew/opportunities/${opportunityId}/directed-invites`,
        { crew_user_ids: Array.from(selected), message },
      );
      const n = (r.data?.sent || []).length;
      setSentToast(`Sent to ${n} ${n === 1 ? 'crew member' : 'crew members'} ✦`);
      setSelected(new Set());
      setMessage('');
      setSuggestions((s) => s.filter((x) => !selected.has(x.crew_user_id)));
      onSent?.();
      setTimeout(() => setSentToast(''), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send invites');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{ marginBottom: 22 }}>
        <button
          onClick={() => setOpen(true)}
          data-testid="invite-previous-crew-open"
          style={{
            padding: '10px 16px', borderRadius: 999, border: `1px solid #FFD16677`,
            cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
            fontSize: 12.5, fontWeight: 800,
            background: 'linear-gradient(135deg, rgba(255,209,102,0.10), rgba(108,59,255,0.10))',
            boxShadow: '0 8px 22px rgba(255,209,102,0.18)',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <Heart size={13} weight="fill" color="#FFD166" />
          Invite previous crew ✦
        </button>
      </div>
    );
  }

  return (
    <section
      data-testid="invite-previous-crew-panel"
      style={{
        marginBottom: 22, padding: 18, borderRadius: 20,
        background: `linear-gradient(135deg, rgba(255,209,102,0.08), rgba(108,59,255,0.08))`,
        border: `1px solid rgba(255,209,102,0.45)`,
        boxShadow: `0 10px 26px rgba(255,209,102,0.20)`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#FFD166' }}>
            ✦ Request again
          </p>
          <p style={{ margin: '4px 0 0', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 18, color: TEXT, letterSpacing: '-0.005em' }}>
            Invite previous crew to {opportunityTitle}
          </p>
        </div>
        <button
          onClick={() => setOpen(false)}
          data-testid="invite-previous-crew-close"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, fontSize: 13,
          }}
        >Close</button>
      </div>

      {loading && <p style={{ marginTop: 10, color: TEXT_MUTED, fontSize: 13 }}>Loading…</p>}

      {!loading && loaded && suggestions.length === 0 && !error && (
        <p
          data-testid="invite-previous-crew-empty"
          style={{ marginTop: 12, color: TEXT_MUTED, fontSize: 13, lineHeight: 1.55 }}
        >
          Nobody to invite back yet — mark a previous applicant as Request again ✦ or
          a Crew Favourite after their shift and they'll appear here.
        </p>
      )}

      {suggestions.length > 0 && (
        <>
          <div
            data-testid="invite-previous-crew-list"
            style={{ display: 'grid', gap: 8, marginTop: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
          >
            {suggestions.map((c) => {
              const isOn = selected.has(c.crew_user_id);
              return (
                <button
                  key={c.crew_user_id}
                  onClick={() => toggle(c.crew_user_id)}
                  data-testid={`invite-row-${c.crew_user_id}`}
                  data-selected={isOn ? '1' : '0'}
                  style={{
                    padding: '10px 12px', borderRadius: 14, cursor: 'pointer',
                    border: `1px solid ${isOn ? '#FFD166AA' : PANEL_BORDER}`,
                    background: isOn ? 'rgba(255,209,102,0.10)' : PANEL,
                    color: TEXT, textAlign: 'left',
                    fontFamily: FONT_STACK,
                    display: 'flex', alignItems: 'center', gap: 10,
                    boxShadow: isOn ? '0 6px 18px rgba(255,209,102,0.30)' : 'none',
                  }}
                >
                  <span style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: c.picture ? `url(${c.picture}) center/cover` : `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 900, fontSize: 13, color: TEXT,
                  }}>
                    {!c.picture && (c.first_name || '?').slice(0, 1).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 800, fontSize: 13.5 }}>{c.name}</span>
                    <span style={{ display: 'block', fontSize: 11, color: TEXT_MUTED }}>
                      {c.is_favourite ? '💛 Favourite' : '✨ Request-again'}
                      {' · Lvl '}{c.crew_level}
                    </span>
                  </span>
                  <CheckCircle size={16} weight={isOn ? 'fill' : 'bold'} color={isOn ? '#FFD166' : TEXT_MUTED} />
                </button>
              );
            })}
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="invite-previous-crew-message"
            placeholder="Optional message — 'Loved having you last time, fancy this one?'"
            rows={2}
            maxLength={240}
            style={{
              marginTop: 12, width: '100%', padding: '10px 12px', borderRadius: 12,
              background: 'rgba(255,255,255,0.04)', border: `1px solid ${PANEL_BORDER}`,
              color: TEXT, fontFamily: FONT_STACK, fontSize: 13.5, outline: 'none',
              resize: 'vertical', lineHeight: 1.5,
            }}
          />
          {error && <p data-testid="invite-previous-crew-error" style={{ margin: '8px 0 0', color: PINK, fontSize: 12.5 }}>{error}</p>}
          {sentToast && <p data-testid="invite-previous-crew-toast" style={{ margin: '8px 0 0', color: '#FFD166', fontSize: 12.5, fontWeight: 700 }}>{sentToast}</p>}
          <div style={{ marginTop: 12 }}>
            <button
              onClick={send}
              disabled={busy || selected.size === 0}
              data-testid="invite-previous-crew-send"
              style={{
                padding: '11px 22px', borderRadius: 999, border: 'none',
                cursor: busy ? 'wait' : (selected.size === 0 ? 'not-allowed' : 'pointer'),
                color: '#1A1100', fontFamily: FONT_STACK,
                fontSize: 13, fontWeight: 900,
                background: `linear-gradient(135deg, #FFD166, #FFB347)`,
                boxShadow: `0 10px 24px rgba(255,209,102,0.5)`,
                opacity: busy || selected.size === 0 ? 0.6 : 1,
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              <Heart size={13} weight="fill" />
              {busy
                ? 'Sending…'
                : selected.size === 0
                  ? 'Pick crew to invite'
                  : `Send invite to ${selected.size}`}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

// ── Repeat-Requested ✦ — Reputation v2 social trust press ──
const RepeatRequestPress = ({ applicationId, applicantName }) => {
  const [state, setState] = useState({ request_again: false, favourite: false, loaded: false });
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crew/applications/${applicationId}/repeat-status`);
      setState({
        request_again: !!r.data?.request_again,
        favourite: !!r.data?.favourite,
        loaded: true,
      });
    } catch {
      setState((s) => ({ ...s, loaded: true }));
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (kind) => {
    if (busy) return;
    setBusy(kind);
    const currently = !!state[kind];
    try {
      if (currently) {
        await axios.delete(`${API}/crew/applications/${applicationId}/request-again`, { params: { kind } });
        setState((s) => ({ ...s, [kind]: false }));
        setToast(kind === 'favourite' ? 'Favourite removed' : 'Request undone');
      } else {
        await axios.post(`${API}/crew/applications/${applicationId}/request-again`, { kind });
        setState((s) => ({ ...s, [kind]: true }));
        const first = (applicantName || '').split(' ')[0] || 'them';
        setToast(
          kind === 'favourite'
            ? `${first} is now a Crew Favourite ✦`
            : `${first} flagged to request again ✦`
        );
      }
    } catch {
      setToast('Could not save — try again.');
    } finally {
      setBusy('');
      setTimeout(() => setToast(''), 2200);
    }
  };

  if (!state.loaded) return null;

  return (
    <div
      data-testid={`repeat-press-${applicationId}`}
      style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
    >
      <button
        onClick={() => toggle('request_again')}
        disabled={busy === 'request_again'}
        data-testid={`repeat-again-${applicationId}`}
        data-on={state.request_again ? '1' : '0'}
        style={{
          padding: '8px 14px', borderRadius: 999,
          border: `1px solid ${state.request_again ? BLUE + '88' : PANEL_BORDER}`,
          background: state.request_again
            ? `linear-gradient(135deg, ${BLUE}33, ${PURPLE}33)`
            : 'rgba(255,255,255,0.04)',
          color: state.request_again ? TEXT : TEXT_MUTED,
          cursor: busy === 'request_again' ? 'wait' : 'pointer',
          fontFamily: FONT_STACK, fontSize: 12, fontWeight: 800,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          boxShadow: state.request_again ? `0 6px 18px ${BLUE}33` : 'none',
          transition: 'all .15s ease',
        }}
      >
        <ArrowClockwise size={12} weight="bold" />
        {state.request_again ? 'Will request again ✦' : 'Request again ✦'}
      </button>
      <button
        onClick={() => toggle('favourite')}
        disabled={busy === 'favourite'}
        data-testid={`favourite-${applicationId}`}
        data-on={state.favourite ? '1' : '0'}
        style={{
          padding: '8px 14px', borderRadius: 999,
          border: `1px solid ${state.favourite ? PINK + '88' : PANEL_BORDER}`,
          background: state.favourite
            ? `linear-gradient(135deg, ${PINK}33, ${PURPLE}33)`
            : 'rgba(255,255,255,0.04)',
          color: state.favourite ? TEXT : TEXT_MUTED,
          cursor: busy === 'favourite' ? 'wait' : 'pointer',
          fontFamily: FONT_STACK, fontSize: 12, fontWeight: 800,
          display: 'inline-flex', alignItems: 'center', gap: 6,
          boxShadow: state.favourite ? `0 6px 18px ${PINK}33` : 'none',
          transition: 'all .15s ease',
        }}
      >
        <Heart size={12} weight={state.favourite ? 'fill' : 'bold'} />
        {state.favourite ? 'Crew Favourite ✦' : 'Mark favourite'}
      </button>
      {toast && (
        <span
          data-testid={`repeat-toast-${applicationId}`}
          style={{
            fontFamily: FONT_STACK, fontSize: 11.5, color: TEXT, fontWeight: 700,
            padding: '5px 10px', borderRadius: 999,
            background: 'rgba(51,195,255,0.10)',
            border: `1px solid ${BLUE}55`,
          }}
        >
          {toast}
        </span>
      )}
    </div>
  );
};

export const SupplierCrewDashboard = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/crew/opportunities/mine`);
      setList(r.data?.opportunities || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/dashboard/supplier/crew');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load opportunities');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Page testId="supplier-crew-dashboard">
      <button
        onClick={() => navigate('/dashboard/supplier')}
        data-testid="crew-dashboard-back"
        style={{
          background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
        }}
      >
        <ArrowLeft size={14} weight="bold" /> Studio
      </button>

      <Eyebrow>✦ EventDrop Crew · Your opportunities</Eyebrow>
      <h1 style={{
        margin: '14px 0 6px', fontWeight: 900, fontSize: 'clamp(28px, 5vw, 42px)',
        letterSpacing: '-0.025em', lineHeight: 1.08,
      }}>
        Reliable crew, ready to swipe.
      </h1>
      <p style={{ color: TEXT_MUTED, fontSize: 14.5, lineHeight: 1.55, marginBottom: 28 }}>
        Post opportunities. Watch applicants land. Tap to accept.
      </p>

      <button
        onClick={() => navigate('/dashboard/supplier/crew/new')}
        data-testid="crew-dashboard-new"
        style={{
          padding: '14px 24px', borderRadius: 999, border: 'none', cursor: 'pointer',
          color: TEXT, fontFamily: FONT_STACK, fontSize: 14, fontWeight: 800,
          background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
          boxShadow: `0 8px 24px ${PURPLE}44`,
          display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 32,
        }}
      >
        <Plus size={14} weight="bold" /> New opportunity
      </button>

      {loading ? (
        <p style={{ color: TEXT_MUTED }}>Loading…</p>
      ) : error ? (
        <p data-testid="crew-dashboard-error" style={{ color: PINK }}>{error}</p>
      ) : list.length === 0 ? (
        <div style={{
          padding: 28, borderRadius: 22, background: PANEL,
          border: `1px solid ${PANEL_BORDER}`, textAlign: 'center',
        }}>
          <Sparkle size={26} weight="fill" color={BLUE} />
          <p style={{ margin: '12px 0 6px', fontWeight: 800, fontSize: 17 }}>No opportunities posted yet</p>
          <p style={{ color: TEXT_MUTED, fontSize: 13.5, margin: 0 }}>
            Your first post takes under 30 seconds.
          </p>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {list.map((o) => (
            <li key={o.id}>
              <button
                onClick={() => navigate(`/dashboard/supplier/crew/${o.id}/applicants`)}
                data-testid={`crew-opp-row-${o.id}`}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: 18, borderRadius: 18, cursor: 'pointer',
                  background: PANEL, border: `1px solid ${PANEL_BORDER}`,
                  color: TEXT, fontFamily: FONT_STACK,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 16, letterSpacing: '-0.01em' }}>
                    {o.title}
                  </p>
                  <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: 13 }}>
                    {o.event_date} · {o.location} · <span style={{ color: PINK, fontWeight: 700 }}>{o.pay}</span>
                  </p>
                </div>
                <div style={{
                  flexShrink: 0, padding: '8px 14px', borderRadius: 999,
                  background: `linear-gradient(135deg, ${BLUE}33, ${PURPLE}33)`,
                  border: `1px solid ${BLUE}66`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  fontSize: 12.5, fontWeight: 800,
                }}>
                  <UsersThree size={13} weight="fill" />
                  {o.applications_count || 0}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
};

export const SupplierApplicantsPage = () => {
  const { opportunityId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ opp: null, applicants: [], loading: true });
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await axios.get(`${API}/crew/opportunities/${opportunityId}/applicants`);
      setState({ opp: r.data?.opportunity, applicants: r.data?.applicants || [], loading: false });
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load applicants');
      setState((s) => ({ ...s, loading: false }));
    }
  }, [opportunityId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  const decide = async (appId, decision) => {
    setBusyId(appId);
    try {
      await axios.post(`${API}/crew/applications/${appId}/decision`, { decision });
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save decision');
    } finally {
      setBusyId(null);
    }
  };

  const messageApplicant = async (appId) => {
    try {
      const r = await axios.post(`${API}/crew/threads`, { application_id: appId });
      if (r.data?.id) navigate(`/crew/chat/${r.data.id}`);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not open thread');
    }
  };

  const { opp, applicants, loading } = state;

  return (
    <Page testId="supplier-applicants-page">
      <button
        onClick={() => navigate('/dashboard/supplier/crew')}
        data-testid="applicants-back"
        style={{
          background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
        }}
      >
        <ArrowLeft size={14} weight="bold" /> Opportunities
      </button>

      {loading ? <p style={{ color: TEXT_MUTED }}>Loading…</p> : opp ? (
        <>
          <Eyebrow>✦ Applicants · {opp.event_date}</Eyebrow>
          <h1 style={{
            margin: '12px 0 6px', fontWeight: 900, fontSize: 'clamp(26px, 5vw, 40px)',
            letterSpacing: '-0.025em', lineHeight: 1.08,
          }}>{opp.title}</h1>
          <p style={{ color: TEXT_MUTED, fontSize: 14, marginBottom: 28 }}>
            {opp.location} · <span style={{ color: PINK, fontWeight: 700 }}>{opp.pay}</span>
          </p>

          {/* Repeat-Requested ✦ — invite previous crew straight onto this shift. */}
          <InvitePreviousCrewModal
            opportunityId={opportunityId}
            opportunityTitle={opp.title}
            onSent={refresh}
          />

          {applicants.length === 0 ? (
            <div style={{
              padding: 28, borderRadius: 22, background: PANEL,
              border: `1px solid ${PANEL_BORDER}`, textAlign: 'center',
            }}>
              <p style={{ margin: '0 0 4px', fontWeight: 800 }}>No applicants yet</p>
              <p style={{ color: TEXT_MUTED, fontSize: 13.5, margin: 0 }}>
                Share the listing or wait for the feed to surface it.
              </p>
            </div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
              {applicants.map((a) => (
                <li
                  key={a.id}
                  data-testid={`applicant-row-${a.id}`}
                  style={{
                    padding: 16, borderRadius: 18,
                    background: PANEL, border: `1px solid ${PANEL_BORDER}`,
                    display: 'flex', flexDirection: 'column', gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: a.crew_picture
                        ? `url(${a.crew_picture}) center/cover`
                        : `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
                      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 900, fontSize: 15, color: TEXT,
                    }}>
                      {!a.crew_picture && (a.crew_name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 800, fontSize: 14.5 }}>{a.crew_name}</p>
                      <p style={{ margin: '2px 0 0', color: TEXT_MUTED, fontSize: 12 }}>
                        {a.crew_email}
                        {a.bring_friend ? ' · 🤝 Bringing a friend' : ''}
                      </p>
                      {a.partner_name && (
                        <p style={{ margin: '2px 0 0', color: TEXT_MUTED, fontSize: 12 }}>
                          Partner: {a.partner_name}
                        </p>
                      )}
                      <ApplicantInsightsStrip
                        insights={a.insights}
                        applicationId={a.id}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {a.status === 'interested' ? (
                        <>
                          <button
                            onClick={() => decide(a.id, 'accepted')}
                            disabled={busyId === a.id}
                            data-testid={`accept-${a.id}`}
                            style={{
                              padding: '8px 12px', borderRadius: 999, border: 'none',
                              cursor: busyId === a.id ? 'wait' : 'pointer',
                              color: TEXT, fontWeight: 800, fontSize: 12.5,
                              background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            <CheckCircle size={12} weight="fill" /> Accept
                          </button>
                          <button
                            onClick={() => decide(a.id, 'declined')}
                            disabled={busyId === a.id}
                            data-testid={`decline-${a.id}`}
                            style={{
                              padding: '8px 12px', borderRadius: 999,
                              cursor: busyId === a.id ? 'wait' : 'pointer',
                              color: TEXT_MUTED, fontWeight: 800, fontSize: 12.5,
                              background: 'transparent', border: `1px solid ${PANEL_BORDER}`,
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            <XIcon size={12} weight="bold" /> Decline
                          </button>
                        </>
                      ) : (
                        <span style={{
                          padding: '6px 12px', borderRadius: 999,
                          fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
                          background: a.status === 'accepted' || a.status === 'attended' ? `${BLUE}33` : 'rgba(255,255,255,0.08)',
                          color: a.status === 'accepted' || a.status === 'attended' ? BLUE : TEXT_MUTED,
                          border: `1px solid ${a.status === 'accepted' || a.status === 'attended' ? BLUE + '66' : PANEL_BORDER}`,
                        }}>
                          {a.status}
                        </span>
                      )}
                    </div>
                  </div>
                  {(a.status === 'accepted' || a.status === 'attended') && (
                    <>
                      <button
                        onClick={() => messageApplicant(a.id)}
                        data-testid={`message-${a.id}`}
                        style={{
                          marginTop: 6, padding: '8px 14px', borderRadius: 999, border: 'none',
                          cursor: 'pointer', color: TEXT, fontFamily: 'inherit',
                          fontSize: 12.5, fontWeight: 800,
                          background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
                          boxShadow: `0 6px 18px ${BLUE}44`,
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          alignSelf: 'flex-start',
                        }}
                      >
                        <ChatCircleDots size={12} weight="fill" /> Message
                      </button>
                      <ReferenceChips
                        applicationId={a.id}
                        applicantName={a.crew_name}
                        onSent={() => refresh()}
                      />
                      <RepeatRequestPress
                        applicationId={a.id}
                        applicantName={a.crew_name}
                      />
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p data-testid="applicants-error" style={{ color: PINK }}>{error || 'Opportunity not found'}</p>
      )}
    </Page>
  );
};

export default SupplierCrewDashboard;
